'use client'

import { Button } from "@/_ui";
import { updateProfileLocale } from "@/actions/user";
import { useParams, usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState, useTransition } from "react";

interface Language {
  id: number;
  code: string;
  name: string;
  flag: string;
}

const languages: Language[] = [
  { id: 1, name: 'English', code: 'en-us', flag: '🇺🇸' },
  { id: 2, name: 'Français', code: 'fr-fr', flag: '🇫🇷' },
  { id: 3, name: 'Español', code: 'es-419', flag: '🇪🇸' },
  { id: 4, name: 'Português', code: 'pt-br', flag: '🇧🇷' },
  { id: 5, name: 'Deutsch', code: 'de-de', flag: '🇩🇪' },
  { id: 6, name: '日本語', code: 'ja-jp', flag: '🇯🇵' }
];

export const Language = ({ profileId }: { profileId?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const currentLocale = params.locale as string;

  const currentLanguage = languages.find(lang => lang.code === currentLocale) || languages[0];

  const handleChange = (newLocale: string) => {
    if (newLocale === currentLocale) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);
    document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;

    if (profileId) {
      startTransition(async () => {
        await updateProfileLocale(profileId, newLocale);
        // The revalidation should happen on the server, but we also need to redirect
        const newPath = pathname.replace(`/${currentLocale}`, `/${newLocale}`);
        router.push(newPath);
      });
    } else {
      const newPath = pathname.replace(`/${currentLocale}`, `/${newLocale}`);
      router.push(newPath);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        type="button"
        variant="reverse"
        icon={<span className="text-lg mr-2" role="img" aria-label={currentLanguage.name}>{currentLanguage.flag}</span>}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={isPending}
      >
        <span className="hidden sm:block">
          {isPending ? '...' : currentLanguage.name}
        </span>
      </Button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="py-1" role="listbox">
            {languages.map((language) => (
              <Button
                key={language.code}
                type="button"
                variant="ghost"
                icon={<span className="text-lg mr-2" role="img" aria-label={language.name}>{language.flag}</span>}
                onClick={() => handleChange(language.code)}
                role="option"
                aria-selected={language.code === currentLocale}
                disabled={isPending || language.code === currentLocale}
              >
                {language.name}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};